import { useEffect, useState } from "react";
import {
  isAnalyticsEnabled,
  getStoredConsent,
  grantConsent,
  denyConsent,
  CONSENT_RESET_EVENT,
} from "@/lib/analytics";

/**
 * Minimal GDPR-compliant consent banner. Renders only when
 *  - analytics is env-enabled AND
 *  - the visitor has never made a choice.
 * Styled to Facetra's matte-black + gold identity so it feels like
 * part of the site, not a slapped-on cookie banner. Bottom-left so
 * it never blocks the hero CTA.
 */
export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isAnalyticsEnabled()) return;
    if (getStoredConsent()) return; // returning visitor — respect prior choice
    // Delay ~1.2s so we don't fight the hero entrance animations.
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, []);

  // "Cookie preferences" footer link → resetConsent() fires this event
  // and the banner pops back open immediately. No page reload needed.
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;
    const onReset = () => setVisible(true);
    window.addEventListener(CONSENT_RESET_EVENT, onReset);
    return () => window.removeEventListener(CONSENT_RESET_EVENT, onReset);
  }, []);

  if (!visible) return null;

  const accept = () => {
    grantConsent();
    setVisible(false);
  };
  const decline = () => {
    denyConsent();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      data-testid="consent-banner"
      className="fixed bottom-4 left-4 z-[100] max-w-md rounded-2xl border border-white/12 bg-[#0B0B0B]/95 backdrop-blur-xl shadow-2xl shadow-black/60 p-5 text-white/85"
      style={{ boxShadow: "0 20px 60px -10px rgba(0,0,0,0.6), 0 0 0 1px rgba(212,175,55,0.08)" }}
    >
      <div className="flex items-start gap-2">
        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
        <div className="flex-1 space-y-3">
          <p className="text-[13px] leading-relaxed text-white/75">
            Facetra uses analytics cookies to understand how ateliers and
            designers use the studio, so we can build the right features next.
            No ads, no cross-site tracking.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={accept}
              data-testid="consent-accept-btn"
              className="px-4 py-1.5 rounded-full bg-[#D4AF37] hover:bg-[#E1BE45] text-black text-[12px] font-medium tracking-wide transition"
            >
              Accept
            </button>
            <button
              type="button"
              onClick={decline}
              data-testid="consent-decline-btn"
              className="px-4 py-1.5 rounded-full border border-white/15 hover:border-white/30 text-white/70 hover:text-white text-[12px] transition"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
